/**
* Copyright by:

* by ESS-Ehrhardt Software Solutions & Service GmbH
* Basselscheider Str. 55
* D-56281 Emmelshausen
* +49 (0) 67 42 / 87 27 0
* info@epg.com
*/
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  Pipe,
  PipeTransform,
  SimpleChanges
} from '@angular/core';
import { SendEmailAction } from '../workflow-action-types/send-email-action';
import { faTrash } from '@fortawesome/pro-solid-svg-icons';
import { EmployeeGroupModel } from '../../../../../../models/employee-group.model';
import { EmployeeModel } from '../../../../../../models/employee.model';
import {
  RecipientOptions,
  RecipientOptionType,
  TriggerTypeIncident
} from '../../../../../../models/workflowNotificationTrigger.model';
import { EmployeeService } from '../../../../../../services/employee.service';
import { Router } from '@angular/router';
import { ChipsElementModel } from '../../../../../../app/shared/chips-autocomplete/chips-autocomplete.component';
import { Logger, LogService } from '@epg/angular-base-lib';
export interface Answer {
  answerText?: string;
  isIncident?: boolean;
  triggerType?: TriggerTypeIncident;
}

export interface IncidentQuestionData {
  questionText?: string;
  answers: Answer[];
}
@Pipe({
  name: 'toAdditionalRecipients',
  standalone: false
})
export class ToAdditionalRecipientsPipe implements PipeTransform {
  transform(value: RecipientOptions | undefined): string[] {
    const transformedValue: string[] = [];

    if (value?.employees && value.employees.length > 0) {
      transformedValue.push(...value.employees);
    }

    if (value?.employeegroups && value.employeegroups.length > 0) {
      transformedValue.push(...value.employeegroups);
    }
    return transformedValue;
  }
}

@Pipe({
  name: 'filterIncidents',
  standalone: false
})
export class FilterIncidentsPipe implements PipeTransform {
  transform(answers: Answer[]): Answer[] {
    return answers.filter((answer) => answer.isIncident);
  }
}

const EMPLOYEE_GROUP_DETAILS_URL = 'Administration/employee-group/';
const EMPLOYEE_DETAILS_URL = 'Administration/employee/';

const ADDITIONAL_RECIPIENTS_AUTOCOMPLETE_PLACEHOLDER = $localize`:@@addAdditionalRecipients:Add additional recipients`;
const EMPLOYEE_GROUP_AUTOCOMPLETE_PLACEHOLDER = $localize`:@@addEmployeeGroups:Add employee groups`;

const ADDITIONAL_RECIPIENTS_TEXT = $localize`:@@additionalRecipients:Additional recipients`;
const EMPLOYEE_GROUPS_TEXT = $localize`:@@employeeGroupsOptional:Employee groups (optional)`;

@Component({
  selector: 'oneapp-send-email-action',
  templateUrl: './send-email-action.component.html',
  styleUrl: './send-email-action.component.scss',
  standalone: false
})
export class SendEmailActionComponent implements OnInit, OnChanges {
  @Input({ required: true })
  action!: SendEmailAction;
  showIncidents: boolean = false;
  readonly faTrash = faTrash;
  @Input() incidentQuestions: IncidentQuestionData[] = [];
  @Input() workflowId: string = '';
  @Input() isDeleting: boolean = false;

  @Output() deleteRequested = new EventEmitter<void>();
  @Output() actionIsChanged = new EventEmitter<void>();

  employeeGroups: EmployeeGroupModel[] = [];
  employees: EmployeeModel[] = [];
  convertedEmployeeGroups: ChipsElementModel[] = [];
  convertedEmployeesAndEmployeeGroups: ChipsElementModel[] = [];

  ADDITIONAL_RECIPIENTS_AUTOCOMPLETE_PLACEHOLDER = ADDITIONAL_RECIPIENTS_AUTOCOMPLETE_PLACEHOLDER;
  EMPLOYEE_GROUP_AUTOCOMPLETE_PLACEHOLDER = EMPLOYEE_GROUP_AUTOCOMPLETE_PLACEHOLDER;

  ADDITIONAL_RECIPIENTS_TEXT = ADDITIONAL_RECIPIENTS_TEXT;
  EMPLOYEE_GROUPS_TEXT = EMPLOYEE_GROUPS_TEXT;

  private skipUnsavedChangeEmit: boolean = false;

  private log: Logger;
  constructor(
    private employeeService: EmployeeService,
    private router: Router,
    private logService: LogService
  ) {
    this.log = logService.getLogger(this);
  }

  async ngOnInit(): Promise<void> {
    this.employees = await this.employeeService.getEmployees();
    this.employeeGroups = await this.employeeService.getEmployeeGroups();
    this.showIncidents = false;

    this.convertedEmployeeGroups = this.convertToChipsElementModelArray(this.employeeGroups);
    let convertedEmployees = this.convertToChipsElementModelArray(this.employees);

    //combine both arrays
    this.convertedEmployeesAndEmployeeGroups =
      this.convertedEmployeeGroups.concat(convertedEmployees);

    //for backward compatibility
    if (this.action.trigger_options?.trigger_types?.incident_trigger_all) {
      this.skipUnsavedChangeEmit = true;
      this.checkAllIncidents(true);
      this.skipUnsavedChangeEmit = false;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['workflowId']) {
      this.showIncidents = false;
    }
  }

  initTriggerTypes(): void {
    if (!this.action.trigger_options) {
      this.action.trigger_options = {};
    }
    if (!this.action.trigger_options?.trigger_types) {
      this.action.trigger_options.trigger_types = {};
    }
    if (!this.action.trigger_options!.trigger_types!.incidents) {
      this.action.trigger_options!.trigger_types!.incidents = [];
    }
  }

  initTriggerGroups(): void {
    if (!this.action.trigger_options) {
      this.action.trigger_options = {};
    }
    if (!this.action.trigger_options?.trigger_groups) {
      this.action.trigger_options.trigger_groups = [];
    }
  }

  initRecipientOptions(): void {
    if (!this.action.recipient_options) {
      this.action.recipient_options = {};
    }
  }

  onDelete(): void {
    this.deleteRequested.emit();
  }

  onTaskClosedTriggerChanged(checked: boolean): void {
    this.initTriggerTypes();
    this.action.trigger_options!.trigger_types!.task_closed = checked;
    this.actionIsChanged.emit();
  }

  onIncidentTriggerChanged(checked: boolean): void {
    this.initTriggerTypes();
    this.action.trigger_options!.trigger_types!.incident_trigger_all = checked;
    this.actionIsChanged.emit();
  }

  onTriggerGroupsChanged(selections: { id: string; type: string }[]): void {
    this.initTriggerGroups();
    this.action.trigger_options!.trigger_groups = selections.map((element) => element.id);
    // Update supervisor recipients state
    this.onRecipientAllSupervisorsChanged(false);
    this.onRecipientTriggerGroupSupervisorsChanged(false);
    this.actionIsChanged.emit();
  }

  onRecipientExecutorChanged(checked: boolean): void {
    this.initRecipientOptions();
    this.action.recipient_options!.executors = checked;
    this.actionIsChanged.emit();
  }

  onRecipientAllSupervisorsChanged(checked: boolean): void {
    this.initRecipientOptions();
    this.action.recipient_options!.all_supervisors = checked;
    this.actionIsChanged.emit();
  }

  onRecipientTriggerGroupSupervisorsChanged(checked: boolean): void {
    this.initRecipientOptions();
    this.action.recipient_options!.trigger_group_supervisors = checked;
    this.actionIsChanged.emit();
  }

  onAdditionalRecipientsChanged(selections: any[]): void {
    this.initRecipientOptions();
    let employeeRecipients: string[] = [];
    let employeegroupRecipients: string[] = [];
    for (const selection of selections) {
      const type = selection.type;
      const value = selection.id;
      switch (type) {
        case RecipientOptionType.EMPLOYEE:
          employeeRecipients.push(value);
          break;
        case RecipientOptionType.EMPLOYEEGROUP:
          employeegroupRecipients.push(value);
          break;
        default:
          continue;
      }
    }
    this.action.recipient_options!.employees = employeeRecipients;
    this.action.recipient_options!.employeegroups = employeegroupRecipients;
    this.actionIsChanged.emit();
  }
  areAllIncidentsSelected(): boolean {
    return (
      this.incidentQuestions.length > 0 &&
      this.incidentQuestions.every(
        (question) =>
          question.answers
            .filter((answer) => answer.isIncident) // Only consider isIncident answers
            .every((answer) => {
              return this.isIncidentSelected(answer);
            }) // Check if all are selected
      )
    );
  }
  isIncidentSelected(answer: Answer): boolean {
    const configuredIncidents = this.action.trigger_options?.trigger_types?.incidents;
    if (answer.triggerType !== undefined && configuredIncidents) {
      return configuredIncidents.some(
        (incident) =>
          incident.data?.value === answer.triggerType?.data?.value &&
          incident.state_id === answer.triggerType?.state_id
      );
    } else return false;
  }

  partiallyComplete(): boolean {
    const totalIncidentAnswers = this.incidentQuestions.reduce(
      (acc, question) => acc + question.answers.filter((answer) => answer.isIncident).length,
      0
    );
    const selectedIncidentAnswers =
      this.action.trigger_options?.trigger_types?.incidents?.length ?? 0;

    const isIndeterminate =
      selectedIncidentAnswers > 0 && selectedIncidentAnswers < totalIncidentAnswers;

    return isIndeterminate;
  }

  toggleAll(checked: boolean): void {
    this.checkAllIncidents(checked);
    this.onIncidentTriggerChanged(checked);
  }

  private checkAllIncidents(checked: boolean) {
    this.incidentQuestions.forEach((question) => {
      question.answers.forEach((answer: Answer) => {
        if (answer.isIncident) {
          this.updateIncidents(checked, answer);
        }
      });
    });
  }

  updateIncidents(checked: boolean, answer: Answer): void {
    this.initTriggerTypes();
    const triggerTypes = this.action.trigger_options?.trigger_types;

    if (!triggerTypes) {
      this.log.error('Trigger types are not defined.');
      return;
    }

    const triggerTypeMatches = (incident: TriggerTypeIncident | undefined) =>
      incident?.state_id === answer.triggerType?.state_id &&
      incident?.data?.value === answer.triggerType?.data?.value;

    if (checked) {
      // Check if the incident is not already in the array before adding
      const exists = triggerTypes.incidents?.some(triggerTypeMatches);

      if (!exists && answer.triggerType) {
        triggerTypes.incidents?.push(answer.triggerType);
      }
    } else {
      //get the incident index to remove it from the array
      const index = triggerTypes.incidents?.findIndex(triggerTypeMatches);

      if (index !== undefined && index >= 0) {
        triggerTypes.incidents?.splice(index, 1);
      }
    }
    if (!this.skipUnsavedChangeEmit) {
      triggerTypes.incident_trigger_all = false;
      this.actionIsChanged.emit();
    }
  }

  onGoToDesigner(): void {
    if (this.workflowId.length > 0) {
      this.router.navigate(['home/workflow-designer/workflow/workflow-details'], {
        queryParams: {
          id: this.workflowId
        }
      });
    }
  }

  getEmployeeName(employee: EmployeeModel | undefined): string {
    return employee && employee.name
      ? employee.name
      : employee?.userName || employee?.employeeId || '';
  }

  convertToChipsElementModelArray(
    array: EmployeeGroupModel[] | EmployeeModel[]
  ): ChipsElementModel[] {
    const ChipsElementModelArray: ChipsElementModel[] = [];
    if (Array.isArray(array)) {
      array.forEach((element) => {
        if ('employeeGroupId' in element) {
          // It's an EmployeeGroupModel
          ChipsElementModelArray.push({
            name: element.name,
            id: element.employeeGroupId,
            type: 'EMPLOYEEGROUP',
            url: EMPLOYEE_GROUP_DETAILS_URL
          });
        } else if ('employeeId' in element) {
          // It's an EmployeeModel
          ChipsElementModelArray.push({
            name: this.getEmployeeName(element),
            id: element.employeeId,
            type: 'EMPLOYEE',
            url: EMPLOYEE_DETAILS_URL
          });
        }
      });
    }
    return ChipsElementModelArray;
  }
}
